/**
 * Pause Confirmation Dialog Component
 *
 * Asks user to confirm before pausing a strategy's monitoring.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import type { StrategyInfo } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';

export interface PauseConfirmDialogProps {
  open: boolean;
  strategy: StrategyInfo | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

export const PauseConfirmDialog: React.FC<PauseConfirmDialogProps> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();

  if (!strategy) return null;

  const symbol = strategy.execution_context?.opportunity?.base ||
    strategy.execution_context?.position?.symbol?.replace('USDT', '') || '-';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.warning,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <PauseCircleOutlineIcon />
        {t('traderDashboard.pauseConfirm.title', { symbol })}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.bg.primary, pt: 3 }}>
        <Typography variant="body1" sx={{ color: theme.text.primary, mb: 2 }}>
          {t('traderDashboard.pauseConfirm.confirmMessage')}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 1 }}>
            {t('traderDashboard.pauseConfirm.description')}
          </Typography>
          <Box component="ul" sx={{ color: theme.text.secondary, pl: 2, m: 0 }}>
            <li>
              <Typography variant="body2">
                {t('traderDashboard.pauseConfirm.stopBuying')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('traderDashboard.pauseConfirm.keepPosition')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('traderDashboard.pauseConfirm.canResume')}
              </Typography>
            </li>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: theme.warning,
            backgroundColor: 'rgba(210, 153, 34, 0.1)',
            p: 1.5,
            borderRadius: 1,
            border: `1px solid ${theme.warning}`,
          }}
        >
          {t('traderDashboard.pauseConfirm.warning')}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: theme.bg.primary,
          borderTop: `1px solid ${theme.border}`,
          p: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.text.secondary }} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: theme.text.primary }} /> : <PauseCircleOutlineIcon />}
          sx={{
            backgroundColor: theme.warning,
            color: theme.bg.primary,
            '&:hover': {
              backgroundColor: '#b8860b',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.bg.secondary,
              color: theme.text.secondary,
            },
          }}
        >
          {loading ? t('traderDashboard.pauseConfirm.pausing') : t('traderDashboard.pauseConfirm.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PauseConfirmDialog;
